import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Shield, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Privacy = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const content = {
    en: {
      title: "PRIVACY POLICY",
      lastUpdated: "Last updated: January 2025",
      sections: [
        {
          title: "1. Data Controller",
          content: "Fight like a GYAL is responsible for the processing of your personal data collected through this website. Contact: fightlikeagyal@gmail.com"
        },
        {
          title: "2. Data We Collect",
          content: "We collect the following information when you place an order:\n• Full name\n• Email address\n• Shipping address\n• Payment information (processed securely by PayPal)\n\nWe do NOT store your payment card details. All payments are processed through PayPal's secure platform."
        },
        {
          title: "3. Purpose of Data Processing",
          content: "Your data is used for:\n• Processing and shipping your orders\n• Sending order confirmations and shipping updates\n• Customer service communications\n• Legal compliance (invoicing, tax records)"
        },
        {
          title: "4. Legal Basis (GDPR)",
          content: "We process your data based on:\n• Contract execution (to fulfill your order)\n• Legal obligations (tax and accounting requirements)\n• Legitimate interest (fraud prevention, improving our services)"
        },
        {
          title: "5. Data Sharing",
          content: "We share your data only with:\n• PayPal (payment processing)\n• Shipping carriers (to deliver your order)\n• Email service provider (order confirmations)\n\nWe do NOT sell your personal data to third parties."
        },
        {
          title: "6. Data Retention",
          content: "We keep your data for:\n• Order data: 10 years (legal requirement for accounting)\n• Email communications: Until you unsubscribe\n• Cookies: See our cookie preferences"
        },
        {
          title: "7. Your Rights (GDPR)",
          content: "You have the right to:\n• Access your personal data\n• Rectify inaccurate data\n• Delete your data (\"right to be forgotten\")\n• Data portability\n• Object to processing\n• Lodge a complaint with a supervisory authority\n\nTo exercise these rights, contact us at fightlikeagyal@gmail.com"
        },
        {
          title: "8. Cookies",
          content: "We use essential cookies for:\n• Shopping cart functionality\n• Language preferences\n• Session management\n\nNo tracking or advertising cookies are used."
        },
        {
          title: "9. Security",
          content: "We implement appropriate security measures including:\n• SSL encryption on all pages\n• Secure payment processing via PayPal\n• Limited access to personal data"
        }
      ]
    },
    es: {
      title: "POLÍTICA DE PRIVACIDAD",
      lastUpdated: "Última actualización: Enero 2025",
      sections: [
        {
          title: "1. Responsable del Tratamiento",
          content: "Fight like a GYAL es responsable del tratamiento de tus datos personales recogidos a través de este sitio web. Contacto: fightlikeagyal@gmail.com"
        },
        {
          title: "2. Datos que Recopilamos",
          content: "Recopilamos la siguiente información cuando realizas un pedido:\n• Nombre completo\n• Dirección de correo electrónico\n• Dirección de envío\n• Información de pago (procesada de forma segura por PayPal)\n\nNO almacenamos los datos de tu tarjeta de pago. Todos los pagos se procesan a través de la plataforma segura de PayPal."
        },
        {
          title: "3. Finalidad del Tratamiento",
          content: "Tus datos se utilizan para:\n• Procesar y enviar tus pedidos\n• Enviar confirmaciones de pedido y actualizaciones de envío\n• Comunicaciones de atención al cliente\n• Cumplimiento legal (facturación, registros fiscales)"
        },
        {
          title: "4. Base Legal (RGPD)",
          content: "Procesamos tus datos basándonos en:\n• Ejecución del contrato (para cumplir tu pedido)\n• Obligaciones legales (requisitos fiscales y contables)\n• Interés legítimo (prevención de fraude, mejora de servicios)"
        },
        {
          title: "5. Compartición de Datos",
          content: "Compartimos tus datos solo con:\n• PayPal (procesamiento de pagos)\n• Transportistas (para entregar tu pedido)\n• Proveedor de email (confirmaciones de pedido)\n\nNO vendemos tus datos personales a terceros."
        },
        {
          title: "6. Conservación de Datos",
          content: "Conservamos tus datos durante:\n• Datos de pedidos: 10 años (requisito legal contable)\n• Comunicaciones por email: Hasta que te des de baja\n• Cookies: Ver preferencias de cookies"
        },
        {
          title: "7. Tus Derechos (RGPD)",
          content: "Tienes derecho a:\n• Acceder a tus datos personales\n• Rectificar datos inexactos\n• Suprimir tus datos (\"derecho al olvido\")\n• Portabilidad de datos\n• Oponerte al tratamiento\n• Presentar una reclamación ante la autoridad de control\n\nPara ejercer estos derechos, contáctanos en fightlikeagyal@gmail.com"
        },
        {
          title: "8. Cookies",
          content: "Utilizamos cookies esenciales para:\n• Funcionalidad del carrito de compra\n• Preferencias de idioma\n• Gestión de sesión\n\nNo utilizamos cookies de seguimiento ni publicitarias."
        },
        {
          title: "9. Seguridad",
          content: "Implementamos medidas de seguridad apropiadas incluyendo:\n• Cifrado SSL en todas las páginas\n• Procesamiento seguro de pagos vía PayPal\n• Acceso limitado a datos personales"
        }
      ]
    },
    fr: {
      title: "POLITIQUE DE CONFIDENTIALITÉ",
      lastUpdated: "Dernière mise à jour : Janvier 2025",
      sections: [
        {
          title: "1. Responsable du Traitement",
          content: "Fight like a GYAL est responsable du traitement de vos données personnelles collectées via ce site web. Contact : fightlikeagyal@gmail.com"
        },
        {
          title: "2. Données Collectées",
          content: "Nous collectons les informations suivantes lors de votre commande :\n• Nom complet\n• Adresse e-mail\n• Adresse de livraison\n• Informations de paiement (traitées de manière sécurisée par PayPal)\n\nNous NE stockons PAS les données de votre carte de paiement. Tous les paiements sont traités via la plateforme sécurisée de PayPal."
        },
        {
          title: "3. Finalité du Traitement",
          content: "Vos données sont utilisées pour :\n• Traiter et expédier vos commandes\n• Envoyer des confirmations de commande et des mises à jour d'expédition\n• Communications du service client\n• Conformité légale (facturation, registres fiscaux)"
        },
        {
          title: "4. Base Légale (RGPD)",
          content: "Nous traitons vos données sur la base de :\n• Exécution du contrat (pour honorer votre commande)\n• Obligations légales (exigences fiscales et comptables)\n• Intérêt légitime (prévention de la fraude, amélioration de nos services)"
        },
        {
          title: "5. Partage des Données",
          content: "Nous partageons vos données uniquement avec :\n• PayPal (traitement des paiements)\n• Transporteurs (pour livrer votre commande)\n• Fournisseur d'e-mail (confirmations de commande)\n\nNous NE vendons PAS vos données personnelles à des tiers."
        },
        {
          title: "6. Conservation des Données",
          content: "Nous conservons vos données pendant :\n• Données de commande : 10 ans (exigence légale comptable)\n• Communications par e-mail : Jusqu'à votre désinscription\n• Cookies : Voir nos préférences de cookies"
        },
        {
          title: "7. Vos Droits (RGPD)",
          content: "Vous avez le droit de :\n• Accéder à vos données personnelles\n• Rectifier les données inexactes\n• Supprimer vos données (« droit à l'oubli »)\n• Portabilité des données\n• Vous opposer au traitement\n• Déposer une plainte auprès d'une autorité de contrôle\n\nPour exercer ces droits, contactez-nous à fightlikeagyal@gmail.com"
        },
        {
          title: "8. Cookies",
          content: "Nous utilisons des cookies essentiels pour :\n• Fonctionnalité du panier\n• Préférences de langue\n• Gestion de session\n\nAucun cookie de suivi ou publicitaire n'est utilisé."
        },
        {
          title: "9. Sécurité",
          content: "Nous mettons en œuvre des mesures de sécurité appropriées, notamment :\n• Chiffrement SSL sur toutes les pages\n• Traitement sécurisé des paiements via PayPal\n• Accès limité aux données personnelles"
        }
      ]
    }
  };

  const c = content[lang] || content.en;

  return (
    <div className="min-h-screen pt-20 bg-[#050505]" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <Shield className="w-12 h-12 text-[#FF0099] mx-auto mb-4" />
          <h1 className="font-heading text-4xl text-white font-bold mb-2">{c.title}</h1>
          <p className="text-[#666666] text-sm">{c.lastUpdated}</p>
        </div>

        <div className="space-y-8">
          {c.sections.map((section, index) => (
            <div key={index} className="bg-[#1A1A1A] border border-[#333333] p-6">
              <h2 className="font-heading text-lg text-white font-bold mb-4">{section.title}</h2>
              <p className="text-[#A3A3A3] text-sm leading-relaxed whitespace-pre-line">{section.content}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link to="/legal" className="text-[#FF0099] hover:text-[#FAFF00] transition-colors text-sm">
            ← Back to Legal Information
          </Link>
        </div>
      </div>
    </div>
  );
};
